"use client";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { NextPage } from "next";
import {
  FaSort,
  FaSortUp,
  FaSortDown,
  FaDownload,
} from "react-icons/fa";
import Link from "next/link";
import { useParams, useSearchParams } from "next/navigation";
import { LoadingTable } from "../../components/LoadingSpinner";
import { exportToCSVWithHeaders } from "@/lib/csvExport";

interface EpitopeSeqData {
  Epitope_Name: string;
  Epitope_Source_Molecule: string;
  Assay: number;

  // Cytotoxic specific fields from backend
  count_protein?: number;
  count_Genome?: number;
  NetMHCpan_41_EL?: number;
  NetMHCpan_41_BA?: number;
  Allele?: string;
  antigen_iri?: string;
  reference_value?: string | number;
  TCRLen?: number;
  pmid?: string;
  // Genome?: string;

  // Helper
  NetMHCIIpan_4_3_EL?: number;
  NetMHCIIpan_4_3_BA?: number;
  Affinity?: string;

  // B Cell
  BepiPred_3_0?: number;
  Chou_Fasman?: number;
  Emini?: number;
  Karplus_Schulz?: number;
  Kolaskar_Tongaonkar?: number;
  Parker?: number;
}

const EpitopePage: NextPage = () => {
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [sortField, setSortField] = useState<keyof EpitopeSeqData | null>(null);
  const [antigenEpiSeq, setAntigenEpiSeq] = useState<EpitopeSeqData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [epitopeSearchTerm, setEpitopeSearchTerm] = useState(""); // ✅ epitope search state
  const [antigenSearchTerm, setAntigenSearchTerm] = useState(""); // ✅ antigen search state
  const [debouncedEpitopeSearch, setDebouncedEpitopeSearch] = useState("");
  const [debouncedAntigenSearch, setDebouncedAntigenSearch] = useState("");
  // Removed assayCounts state - now using Assay field directly from API response
  const [epitopeType, setEpitopeType] = useState(""); // ✅ type filter state
  const [hasReferences, setHasReferences] = useState<string>(""); // ✅ has_references filter state
  
  // Autocomplete states
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [allAntigens, setAllAntigens] = useState<string[]>([]);

  // Filter states for min-max ranges
  const [filters, setFilters] = useState({
    lengthMin: "",
    lengthMax: "",
    // Cytotoxic filters
    netMHCpanELMin: "",
    netMHCpanELMax: "",
    netMHCpanBAMin: "",
    netMHCpanBAMax: "",
    tcrLenMin: "",
    tcrLenMax: "",
    // Helper filters
    netMHCIIpanELMin: "",
    netMHCIIpanELMax: "",
    netMHCIIpanBAMin: "",
    netMHCIIpanBAMax: "",
    affinityMin: "",
    affinityMax: "",
    // B Cell filters
    bepiPredMin: "",
    bepiPredMax: "",
    chouFasmanMin: "",
    chouFasmanMax: "",
    eminiMin: "",
    eminiMax: "",
    karplusMin: "",
    karplusMax: "",
    kolaskarMin: "",
    kolaskarMax: "",
    parkerMin: "",
    parkerMax: "",
  });

  type TabType = "Cytotoxic" | "Helper" | "B";
  const [activeTab, setActiveTab] = useState<TabType>("Cytotoxic");
  const [isLoading, setIsLoading] = useState(true);
  const [expandedAlleles, setExpandedAlleles] = useState<Set<number>>(new Set());

  // record counts
  const [cytotoxicCount, setCytotoxicCount] = useState(0);
  const [helperCount, setHelperCount] = useState(0);
  const [bcellCount, setBcellCount] = useState(0);
  const [initialRefLoaded, setInitialRefLoaded] = useState(false);

  const params = useParams<{ slug: string }>();
  const searchParams = useSearchParams();
  let epitope_source_name = decodeURIComponent(params.slug).trim();
  if (epitope_source_name === "all") epitope_source_name = "";

  // Fetch all antigens for autocomplete
  const fetchAllAntigens = async () => {
    try {
      const response = await fetch('/api/antigen-epitope-all?page=1&page_size=1000');
      if (response.ok) {
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          const antigens = result.data.map((item: { Antigen_Name: string }) => item.Antigen_Name).filter(Boolean);
          setAllAntigens(antigens);
        }
      }
    } catch (error) {
      console.error('Failed to fetch antigens:', error);
    }
  };

  // Filter suggestions based on antigen search term
  const filterSuggestions = useCallback((term: string) => {
    if (term.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    const filtered = allAntigens.filter(antigen =>
      antigen.toLowerCase().includes(term.toLowerCase())
    ).slice(0, 10); // Limit to 10 suggestions
    
    setSuggestions(filtered);
    setShowSuggestions(filtered.length > 0);
  }, [allAntigens]);

  const fetchAllEpitopeSequences = async (pageNum: number = 1) => {
    setIsLoading(true);
    try {

      const params = new URLSearchParams({
        tab: activeTab,
        page: pageNum.toString(),
        page_size: recordsPerPage.toString()
      });

      if (debouncedEpitopeSearch) {
        params.set('epitope_name', debouncedEpitopeSearch);
      }
      if (debouncedAntigenSearch) {
        params.set('antigen_name', debouncedAntigenSearch);
      }
      
      // ✅ Add type parameter if it exists
      if (epitopeType) {
        params.set('type', epitopeType);
      }
      
      if (hasReferences) {
        params.set('has_references', hasReferences);
      }
      
      // ✅ Add sort parameters
      if (sortField) {
        params.set('sort_field', sortField);
        params.set('sort_direction', sortDirection);
      }

      const response = await fetch(`/api/epitope-list?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();

      if (result && result.data && Array.isArray(result.data)) {
        // Server-side pagination response
        // Assay counts are now included directly in the API response
        setAntigenEpiSeq(result.data);
        setTotalRecords(result.pagination?.total_count || 0);
        setCurrentPage(result.pagination?.page || pageNum);

        if (result.totals_by_tab) {
          setCytotoxicCount(result.totals_by_tab.Cytotoxic ?? 0);
          setHelperCount(result.totals_by_tab.Helper ?? 0);
          setBcellCount(result.totals_by_tab.B ?? 0);
        }

        // Update counts based on current tab
        if (activeTab === "Cytotoxic") {
          setCytotoxicCount(result.pagination?.total_count || 0);
        } else if (activeTab === "Helper") {
          setHelperCount(result.pagination?.total_count || 0);
        } else {
          setBcellCount(result.pagination?.total_count || 0);
        }

      } else if (Array.isArray(result)) {
        // Fallback for old API format
        setAntigenEpiSeq(result);
        setTotalRecords(result.length);
      } else {
        // Handle unexpected response format
        console.warn("Unexpected API response format:", result);
        setAntigenEpiSeq([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error("Error fetching epitope sequences:", error);
      setAntigenEpiSeq([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!initialRefLoaded) {
      const urlHasReferences = searchParams.get("has_references");
      const urlType = searchParams.get("type");
      const urlSearchTerm = searchParams.get("search");

      if (urlHasReferences) setHasReferences(urlHasReferences);
      if (urlType) setEpitopeType(urlType);
      if (urlSearchTerm) setEpitopeSearchTerm(urlSearchTerm);

      setInitialRefLoaded(true); // ✅ ทำครั้งเดียว
    }
  }, [searchParams, initialRefLoaded]);

  useEffect(() => {
    if (initialRefLoaded) {
      fetchAllEpitopeSequences(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedEpitopeSearch,
    debouncedAntigenSearch,
    epitopeType,
    hasReferences,
    sortField,
    sortDirection,
    currentPage,
    initialRefLoaded,
  ]);

  // Fetch antigens on component mount
  useEffect(() => {
    fetchAllAntigens();
  }, []);

  // Debounce epitope search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedEpitopeSearch(epitopeSearchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [epitopeSearchTerm]);

  // Debounce antigen search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAntigenSearch(antigenSearchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [antigenSearchTerm]);

  // Filter suggestions when antigen search term changes
  useEffect(() => {
    filterSuggestions(antigenSearchTerm);
  }, [antigenSearchTerm, allAntigens, filterSuggestions]);

  // Reset expanded alleles when page or tab changes
  useEffect(() => {
    setExpandedAlleles(new Set());
  }, [currentPage, activeTab]);

  // Reset page when search terms or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedEpitopeSearch, debouncedAntigenSearch, sortField, sortDirection]);

  const handleSort = (field: keyof EpitopeSeqData) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    if (activeTab === "Cytotoxic") {
      setEpitopeType("Cytotoxic");
    } else if (activeTab === "Helper") {
      setEpitopeType("Helper");
    } else if (activeTab === "B") {
      setEpitopeType("B");
    }
  }, [activeTab]);

  const handleRecordsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRecordsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const SortIcon = ({ field }: { field: keyof EpitopeSeqData }) => {
    if (sortField !== field) return <FaSort className="inline ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="inline ml-1" />
    ) : (
      <FaSortDown className="inline ml-1" />
    );
  };

  // Apply filters to the data
  const filteredRecords = useMemo(() => {
    return antigenEpiSeq.filter((row) => {
      // Epitope length filter
      const epitopeLength = row.Epitope_Name.replace(/\s+/g, "").length;
      if (filters.lengthMin && epitopeLength < parseFloat(filters.lengthMin)) return false;
      if (filters.lengthMax && epitopeLength > parseFloat(filters.lengthMax)) return false;

      // Cytotoxic filters
      if (activeTab === "Cytotoxic") {
        if (filters.netMHCpanELMin && (row.NetMHCpan_41_EL == null || row.NetMHCpan_41_EL < parseFloat(filters.netMHCpanELMin))) return false;
        if (filters.netMHCpanELMax && (row.NetMHCpan_41_EL == null || row.NetMHCpan_41_EL > parseFloat(filters.netMHCpanELMax))) return false;
        if (filters.netMHCpanBAMin && (row.NetMHCpan_41_BA == null || row.NetMHCpan_41_BA < parseFloat(filters.netMHCpanBAMin))) return false;
        if (filters.netMHCpanBAMax && (row.NetMHCpan_41_BA == null || row.NetMHCpan_41_BA > parseFloat(filters.netMHCpanBAMax))) return false;
        if (filters.tcrLenMin && (row.TCRLen == null || row.TCRLen < parseFloat(filters.tcrLenMin))) return false;
        if (filters.tcrLenMax && (row.TCRLen == null || row.TCRLen > parseFloat(filters.tcrLenMax))) return false;
      }

      // Helper filters
      if (activeTab === "Helper") {
        if (filters.netMHCIIpanELMin && (row.NetMHCIIpan_4_3_EL == null || row.NetMHCIIpan_4_3_EL < parseFloat(filters.netMHCIIpanELMin))) return false;
        if (filters.netMHCIIpanELMax && (row.NetMHCIIpan_4_3_EL == null || row.NetMHCIIpan_4_3_EL > parseFloat(filters.netMHCIIpanELMax))) return false;
        if (filters.netMHCIIpanBAMin && (row.NetMHCIIpan_4_3_BA == null || row.NetMHCIIpan_4_3_BA < parseFloat(filters.netMHCIIpanBAMin))) return false;
        if (filters.netMHCIIpanBAMax && (row.NetMHCIIpan_4_3_BA == null || row.NetMHCIIpan_4_3_BA > parseFloat(filters.netMHCIIpanBAMax))) return false;
      }

      // B Cell filters
      if (activeTab === "B") {
        if (filters.bepiPredMin && (row.BepiPred_3_0 == null || row.BepiPred_3_0 < parseFloat(filters.bepiPredMin))) return false;
        if (filters.bepiPredMax && (row.BepiPred_3_0 == null || row.BepiPred_3_0 > parseFloat(filters.bepiPredMax))) return false;
        if (filters.chouFasmanMin && (row.Chou_Fasman == null || row.Chou_Fasman < parseFloat(filters.chouFasmanMin))) return false;
        if (filters.chouFasmanMax && (row.Chou_Fasman == null || row.Chou_Fasman > parseFloat(filters.chouFasmanMax))) return false;
        if (filters.eminiMin && (row.Emini == null || row.Emini < parseFloat(filters.eminiMin))) return false;
        if (filters.eminiMax && (row.Emini == null || row.Emini > parseFloat(filters.eminiMax))) return false;
        if (filters.karplusMin && (row.Karplus_Schulz == null || row.Karplus_Schulz < parseFloat(filters.karplusMin))) return false;
        if (filters.karplusMax && (row.Karplus_Schulz == null || row.Karplus_Schulz > parseFloat(filters.karplusMax))) return false;
        if (filters.kolaskarMin && (row.Kolaskar_Tongaonkar == null || row.Kolaskar_Tongaonkar < parseFloat(filters.kolaskarMin))) return false;
        if (filters.kolaskarMax && (row.Kolaskar_Tongaonkar == null || row.Kolaskar_Tongaonkar > parseFloat(filters.kolaskarMax))) return false;
        if (filters.parkerMin && (row.Parker == null || row.Parker < parseFloat(filters.parkerMin))) return false;
        if (filters.parkerMax && (row.Parker == null || row.Parker > parseFloat(filters.parkerMax))) return false;
      }

      return true;
    });
  }, [antigenEpiSeq, filters, activeTab]);

  // Use server-side sorting - data already sorted by backend
  const currentRecords = filteredRecords;
  const filteredCount = filteredRecords.length;
  const totalPages = Math.ceil(totalRecords / recordsPerPage);

  // Count active filters
  const activeFiltersCount = Object.values(filters).filter(v => v !== "").length;

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const toggleAlleleExpansion = (index: number) => {
    setExpandedAlleles(prev => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  // Helper functions to get color class based on score
  const getNetMHCpanELColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "";
    if (score > 0.95) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 0.90) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getNetMHCpanBAColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "";
    if (score < 100) return "bg-green-100 text-green-800 font-semibold";
    if (score <= 500) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getTCRLensColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "";
    if (score > 0.7) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 0.5) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getNetMHCIIpanELColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "";
    if (score > 0.95) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 0.90) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getNetMHCIIpanBAColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "";
    if (score < 100) return "bg-green-100 text-green-800 font-semibold";
    if (score <= 500) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getBepiPredColor = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return "";
    if (score > 0.5) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 0.3) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getChouFasmanColor = (score: number | null | undefined): string => {
    if (score == null) return "";
    if (score > 1.2) return "bg-green-100 text-green-800 font-semibold";       // High-confidence
    if (score >= 1.0) return "bg-orange-100 text-orange-800 font-semibold";    // Putative
    return "bg-red-100 text-red-800 font-semibold";                            // Weak-binding
  };

  const getEminiColor = (score: number | null | undefined): string => {
    if (score == null) return "";
    if (score > 4.0) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 2.0) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getKarplusSchulzColor = (score: number | null | undefined): string => {
    if (score == null) return "";
    if (score > 1.1) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 1.0) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getKolaskarTongaonkarColor = (score: number | null | undefined): string => {
    if (score == null) return "";
    if (score > 1.1) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 1.0) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const getParkerColor = (score: number | null | undefined): string => {
    if (score == null) return "";
    if (score > 5.0) return "bg-green-100 text-green-800 font-semibold";
    if (score >= 2.0) return "bg-orange-100 text-orange-800 font-semibold";
    return "bg-red-100 text-red-800 font-semibold";
  };

  const handleExportCSV = () => {
    const csvData = currentRecords.map(item => {
      const baseData: any = {
        "Epitope": item.Epitope_Name,
        "Proteins Found": item.count_protein ?? 0,
        "Genomes Found": item.count_Genome ?? 0,
        "Antigen (Protein Name)": item.Epitope_Source_Molecule,
        "Assay": item.Assay ?? 0,
      };

      if (activeTab === "Cytotoxic") {
        return {
          ...baseData,
          "NetMHCpan-4.1 EL": item.NetMHCpan_41_EL ?? "NA",
          "NetMHCpan-4.1 BA": item.NetMHCpan_41_BA ?? "NA",
          "TCRLens": item.TCRLen ?? "NA",
          "Allele": item.Allele ?? "-",
          "References (PMID)": item.pmid ?? "-",
        };
      } else if (activeTab === "Helper") {
        return {
          ...baseData,
          "NetMHCIIpan-4.3 EL": item.NetMHCIIpan_4_3_EL ?? "NA",
          "NetMHCIIpan-4.3 BA": item.NetMHCIIpan_4_3_BA ?? "NA",
          "Affinity": item.Affinity ?? "NA",
          "Allele": item.Allele ?? "-",
        };
      } else {
        return {
          ...baseData,
          "BepiPred 3.0": item.BepiPred_3_0 ?? "NA",
          "Chou & Fasman": item.Chou_Fasman ?? "NA",
          "Emini": item.Emini ?? "NA",
          "Karplus & Schulz": item.Karplus_Schulz ?? "NA",
          "Kolaskar & Tongaonkar": item.Kolaskar_Tongaonkar ?? "NA",
          "Parker": item.Parker ?? "NA",
        };
      }
    });

    const typeFilter = epitopeType ? `-${epitopeType}` : '';
    exportToCSVWithHeaders(
      csvData,
      `epitope-${activeTab.toLowerCase()}${typeFilter}-${new Date().toISOString().split('T')[0]}`
    );
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Title with filter indication */}
      <h1 className="text-3xl font-bold text-gray-800 mb-2">
        Epitope Database
        {hasReferences === 'false' && (
          <span className="ml-4 text-base font-normal text-gray-600 bg-green-100 px-3 py-1 rounded-full inline-flex items-center">
            Predicted Epitopes (No References)
            <button
              onClick={() => setHasReferences("")}
              className="ml-2 text-green-600 hover:text-green-800 font-bold"
              title="Clear filter"
            >
              ✕
            </button>
          </span>
        )}
        {hasReferences === 'true' && (
          <span className="ml-4 text-base font-normal text-gray-600 bg-blue-100 px-3 py-1 rounded-full inline-flex items-center">
            Experimental Verified Epitopes (Has References)
            <button
              onClick={() => setHasReferences("")}
              className="ml-2 text-blue-600 hover:text-blue-800 font-bold"
              title="Clear filter"
            >
              ✕
            </button>
          </span>
        )}
      </h1>
      
      {/* Legend for Cytotoxic T Cell scores */}
      {activeTab === "Cytotoxic" && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-gray-800 mb-2">Score Color Legend:</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <div>
              <span className="font-medium">NetMHCpan-4.1 EL:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold" title="Strong binder">&gt; 0.95 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold" title="Weak binder">0.90 - 0.95 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold" title="Non-binder">&lt; 0.90 (Weak-binding)</span>
              </div>
            </div>
            <div>
              <span className="font-medium">NetMHCpan-4.1 BA:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold" title="Strong binder">&lt; 100 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold" title="Weak binder">100 - 500 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold" title="Non-binder">&gt; 500 (Weak-binding)</span>
              </div>
            </div>
            <div>
              <span className="font-medium">TCRLens:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold" title="High immunogenicity">&gt; 0.7 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold" title="Moderate immunogenicity">0.5 - 0.7 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold" title="Low immunogenicity">&lt; 0.5 (Weak-binding)</span>
              </div>
            </div>
          </div>
          <div className="mt-3 flex justify-start">
            <Link href="/instructions" className="inline-block px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 text-sm font-medium">
              More info →
            </Link>
          </div>
        </div>
      )}

      {/* Legend for Helper T Cell scores */}
      {activeTab === "Helper" && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-gray-800 mb-2">Score Color Legend:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="font-medium">NetMHCIIpan-4.3 EL:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold" title="Strong binder">&gt; 5 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold" title="Weak binder">2 - 5 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold" title="Non-binder">&lt; 2 (Weak-binding)</span>
              </div>
            </div>
            <div>
              <span className="font-medium">NetMHCIIpan-4.3 BA:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold" title="Strong binder">&lt; 100 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold" title="Weak binder">100 - 500 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold" title="Non-binder">&gt; 500 (Weak-binding)</span>
              </div>
            </div>
          </div>
          <div className="mt-3 flex justify-start">
            <Link href="/instructions" className="inline-block px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 text-sm font-medium">
              More info →
            </Link>
          </div>
        </div>
      )}

      {/* Legend for B Cell scores */}
      {activeTab === "B" && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h3 className="font-semibold text-gray-800 mb-2">Score Color Legend:</h3>
          <div className="text-sm">
            <div>
              <span className="font-medium">BepiPred 3.0:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold" title="High probability">&gt; 0.5 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold" title="Moderate probability">0.3 - 0.5 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold" title="Low probability">&lt; 0.3 (Weak-binding)</span>
              </div>
            </div>
            <div>
              <span className="font-medium">BepiPred 3.0:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">&gt; 0.5 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold">0.3 - 0.5 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">&lt; 0.3 (Weak-binding)</span>
              </div>
            </div>

            <div>
              <span className="font-medium">Chou & Fasman:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">&gt; 1.2 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold">1.0 - 1.2 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">&lt; 1.0 (Weak-binding)</span>
              </div>
            </div>

            <div>
              <span className="font-medium">Emini:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">&gt; 4 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold">2 - 4 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">&lt; 2 (Weak-binding)</span>
              </div>
            </div>

            <div>
              <span className="font-medium">Karplus & Schulz:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">&gt; 1.1 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold">1.0 - 1.1 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">&lt; 1.0 (Weak-binding)</span>
              </div>
            </div>

            <div>
              <span className="font-medium">Kolaskar & Tongaonkar:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">&gt; 1.1 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold">1.0 - 1.1 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">&lt; 1.0 (Weak-binding)</span>
              </div>
            </div>

            <div>
              <span className="font-medium">Parker:</span>
              <div className="flex items-center gap-2 mt-1">
                <span className="px-2 py-1 rounded bg-green-100 text-green-800 font-semibold">&gt; 5 (High-confidence)</span>
                <span className="px-2 py-1 rounded bg-orange-100 text-orange-800 font-semibold">2 - 5 (Putative)</span>
                <span className="px-2 py-1 rounded bg-red-100 text-red-800 font-semibold">&lt; 2 (Weak-binding)</span>
              </div>
            </div>

          </div>
          <div className="mt-3 flex justify-start">
            <Link href="/instructions" className="inline-block px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 text-sm font-medium">
              More info →
            </Link>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 flex space-x-4">
        <button
          className={`px-4 py-2 rounded ${
            activeTab === "Cytotoxic" ? "bg-blue-600 text-white" : "bg-gray-200 text-black"
          }`}
          onClick={() => setActiveTab("Cytotoxic")}
        >
          Cytotoxic T Cell
          {!isLoading && (
            <> ({activeTab === "Cytotoxic" ? totalRecords : cytotoxicCount})</>
          )}
        </button>
        <button
          className={`px-4 py-2 rounded ${
            activeTab === "Helper" ? "bg-blue-600 text-white" : "bg-gray-200 text-black"
          }`}
          onClick={() => setActiveTab("Helper")}
        >
          Helper T Cell
          {!isLoading && (
            <> ({activeTab === "Helper" ? totalRecords : helperCount})</>
          )}
        </button>
        <button
          className={`px-4 py-2 rounded ${
            activeTab === "B" ? "bg-blue-600 text-white" : "bg-gray-200 text-black"
          }`}
          onClick={() => setActiveTab("B")}
        >
          B Cell
          {!isLoading && (
            <> ({activeTab === "B" ? totalRecords : bcellCount})</>
          )}
        </button>
      </div>

      {/* 🔎 Search Boxes - Separate for Epitope and Antigen */}
      <div className="mb-4 flex gap-4 items-center flex-wrap">
        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">Search by Epitope Name</label>
          <input
            type="text"
            placeholder="e.g., KVDEFYYKY..."
            className="border px-3 py-2 rounded w-64 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-8"
            value={epitopeSearchTerm}
            onChange={(e) => {
              setEpitopeSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
          {epitopeSearchTerm && (
            <button
              onClick={() => setEpitopeSearchTerm("")}
              className="absolute right-2 top-9 text-gray-400 hover:text-gray-600 text-lg font-bold"
              title="Clear epitope search"
            >
              ✕
            </button>
          )}
        </div>

        <div className="relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">Search by Antigen/Protein Name</label>
          <input
            type="text"
            placeholder="e.g., DNA polymerase..."
            className="border px-3 py-2 rounded w-64 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 pr-8"
            value={antigenSearchTerm}
            onChange={(e) => {
              setAntigenSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            onFocus={() => {
              if (suggestions.length > 0) setShowSuggestions(true);
            }}
            onBlur={() => {
              // Delay hiding to allow clicking on suggestions
              setTimeout(() => setShowSuggestions(false), 200);
            }}
          />
          {antigenSearchTerm && (
            <button
              onClick={() => setAntigenSearchTerm("")}
              className="absolute right-2 top-9 text-gray-400 hover:text-gray-600 text-lg font-bold"
              title="Clear antigen search"
            >
              ✕
            </button>
          )}
          
          {/* Autocomplete Suggestions */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute z-10 w-64 bg-white border border-gray-300 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
              {suggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                  onClick={() => {
                    setAntigenSearchTerm(suggestion);
                    setShowSuggestions(false);
                    setCurrentPage(1);
                  }}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>

        {(epitopeSearchTerm || antigenSearchTerm) && (
          <div className="text-xs text-gray-500 italic">
            💡 Tip: Use one search box at a time. Typing in one will clear the other.
          </div>
        )}

        <div className="flex-grow"></div>

        <button
          onClick={handleExportCSV}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors shadow-sm"
          title="Export to CSV"
        >
          <FaDownload className="w-4 h-4" />
          Export CSV
        </button>

        <div className="text-sm text-gray-600">
          {filteredCount} of {totalRecords} records
          {debouncedEpitopeSearch && (
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
              Epitope: &quot;{debouncedEpitopeSearch}&quot;
            </span>
          )}
          {debouncedAntigenSearch && (
            <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
              Antigen: &quot;{debouncedAntigenSearch}&quot;
            </span>
          )}
          {filteredCount < totalRecords && !debouncedEpitopeSearch && !debouncedAntigenSearch && activeFiltersCount > 0 && (
            <span className="ml-2 px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
              Filtered by {activeFiltersCount} {activeFiltersCount === 1 ? 'filter' : 'filters'}
            </span>
          )}
        </div>
      </div>

      {/* Filters Section */}
      <div className="mb-4 p-4 bg-gray-50 border rounded-lg space-y-3">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-700">Filters</h3>
            {activeFiltersCount > 0 && (
              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                {activeFiltersCount} active
              </span>
            )}
          </div>
          {activeFiltersCount > 0 && (
            <button
              onClick={() => setFilters({
                lengthMin: "",
                lengthMax: "",
                netMHCpanELMin: "",
                netMHCpanELMax: "",
                netMHCpanBAMin: "",
                netMHCpanBAMax: "",
                tcrLenMin: "",
                tcrLenMax: "",
                netMHCIIpanELMin: "",
                netMHCIIpanELMax: "",
                netMHCIIpanBAMin: "",
                netMHCIIpanBAMax: "",
                affinityMin: "",
                affinityMax: "",
                bepiPredMin: "",
                bepiPredMax: "",
                chouFasmanMin: "",
                chouFasmanMax: "",
                eminiMin: "",
                eminiMax: "",
                karplusMin: "",
                karplusMax: "",
                kolaskarMin: "",
                kolaskarMax: "",
                parkerMin: "",
                parkerMax: "",
              })}
              className="text-sm px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition shadow-sm"
            >
              Clear All Filters
            </button>
          )}
        </div>
        
        {activeFiltersCount === 0 && (
          <p className="text-xs text-gray-500 italic">
            💡 Use filters below to narrow down results by score ranges. Leave blank for no filtering.
          </p>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Epitope Length Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Epitope Length (amino acids)
            </label>
            <div className="flex gap-2">
              <input
                type="number"
                placeholder="Min"
                className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="1"
                value={filters.lengthMin}
                onChange={(e) => setFilters({ ...filters, lengthMin: e.target.value })}
              />
              <span className="self-center">-</span>
              <input
                type="number"
                placeholder="Max"
                className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="1"
                value={filters.lengthMax}
                onChange={(e) => setFilters({ ...filters, lengthMax: e.target.value })}
              />
            </div>
          </div>

          {/* Score Filters - Dynamic based on active tab */}
          {activeTab === "Cytotoxic" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NetMHCpan-4.1 EL
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCpanELMin}
                    onChange={(e) => setFilters({ ...filters, netMHCpanELMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCpanELMax}
                    onChange={(e) => setFilters({ ...filters, netMHCpanELMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NetMHCpan-4.1 BA
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCpanBAMin}
                    onChange={(e) => setFilters({ ...filters, netMHCpanBAMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCpanBAMax}
                    onChange={(e) => setFilters({ ...filters, netMHCpanBAMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  TCRLen
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.tcrLenMin}
                    onChange={(e) => setFilters({ ...filters, tcrLenMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.tcrLenMax}
                    onChange={(e) => setFilters({ ...filters, tcrLenMax: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === "B" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BepiPred 3.0
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.bepiPredMin}
                    onChange={(e) => setFilters({ ...filters, bepiPredMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.bepiPredMax}
                    onChange={(e) => setFilters({ ...filters, bepiPredMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chou & Fasman
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.chouFasmanMin}
                    onChange={(e) => setFilters({ ...filters, chouFasmanMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.chouFasmanMax}
                    onChange={(e) => setFilters({ ...filters, chouFasmanMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Emini
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.eminiMin}
                    onChange={(e) => setFilters({ ...filters, eminiMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.eminiMax}
                    onChange={(e) => setFilters({ ...filters, eminiMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Karplus & Schulz
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.karplusMin}
                    onChange={(e) => setFilters({ ...filters, karplusMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.karplusMax}
                    onChange={(e) => setFilters({ ...filters, karplusMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kolaskar & Tongaonkar
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.kolaskarMin}
                    onChange={(e) => setFilters({ ...filters, kolaskarMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.kolaskarMax}
                    onChange={(e) => setFilters({ ...filters, kolaskarMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parker
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.parkerMin}
                    onChange={(e) => setFilters({ ...filters, parkerMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.parkerMax}
                    onChange={(e) => setFilters({ ...filters, parkerMax: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}

          {activeTab === "Helper" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NetMHCIIpan-4.3 EL
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCIIpanELMin}
                    onChange={(e) => setFilters({ ...filters, netMHCIIpanELMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCIIpanELMax}
                    onChange={(e) => setFilters({ ...filters, netMHCIIpanELMax: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NetMHCIIpan-4.3 BA
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Min"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCIIpanBAMin}
                    onChange={(e) => setFilters({ ...filters, netMHCIIpanBAMin: e.target.value })}
                  />
                  <span className="self-center">-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    className="border px-2 py-1 rounded w-20 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    step="0.01"
                    value={filters.netMHCIIpanBAMax}
                    onChange={(e) => setFilters({ ...filters, netMHCIIpanBAMax: e.target.value })}
                  />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full border-collapse table-auto">
          <thead>
            <tr className="bg-gray-100">
              <th
                className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("Epitope_Name")}
                title="Click to sort by Epitope Name"
              >
                Epitope <SortIcon field="Epitope_Name" />
              </th>
              <th
                className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("count_protein")}
                title="Click to sort by Proteins Found"
              >
                Proteins Found <SortIcon field="count_protein" />
              </th>
              <th
                className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("count_Genome")}
                title="Click to sort by Genomes Found"
              >
               Genomes Found <SortIcon field="count_Genome" />
              </th>
              <th
                className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("Epitope_Source_Molecule")}
                title="Click to sort by Antigen Name"
              >
                Antigen (Protein Name) <SortIcon field="Epitope_Source_Molecule" />
              </th>
              <th
                className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                onClick={() => handleSort("Assay")}
                title="Click to sort by Assay-Verified"
              >
                Assay <SortIcon field="Assay" />
              </th>
              {activeTab === "Cytotoxic" && (
                <>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("NetMHCpan_41_EL")}
                    title="Click to sort by NetMHCpan-4.1 EL score"
                  >
                    NetMHCpan-4.1 EL <SortIcon field="NetMHCpan_41_EL" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("NetMHCpan_41_BA")}
                    title="Click to sort by NetMHCpan-4.1 BA score"
                  >
                    NetMHCpan-4.1 BA <SortIcon field="NetMHCpan_41_BA" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("TCRLen")}
                    title="Click to sort by TCR Length"
                  >
                    TCRLens <SortIcon field="TCRLen" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Allele")}
                    title="Click to sort by Allele"
                  >
                    Allele <SortIcon field="Allele" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("pmid")}
                    title="Click to sort by PMID (References)"
                  >
                    References (PMID) <SortIcon field="pmid" />
                  </th>
                </>
              )}
              {activeTab === "Helper" && (
                <>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("NetMHCIIpan_4_3_EL")}
                    title="Click to sort by NetMHCIIpan-4.3 EL score"
                  >
                    NetMHCIIpan-4.3 EL <SortIcon field="NetMHCIIpan_4_3_EL" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("NetMHCIIpan_4_3_BA")}
                    title="Click to sort by NetMHCIIpan-4.3 BA score"
                  >
                    NetMHCIIpan-4.3 BA <SortIcon field="NetMHCIIpan_4_3_BA" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Affinity")}
                    title="Click to sort by Affinity"
                  >
                    Affinity <SortIcon field="Affinity" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Allele")}
                    title="Click to sort by Allele"
                  >
                    Allele <SortIcon field="Allele" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("pmid")}
                    title="Click to sort by PMID (References)"
                  >
                    References (PMID) <SortIcon field="pmid" />
                  </th>
                </>
              )}
              {activeTab === "B" && (
                <>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("BepiPred_3_0")}
                    title="Click to sort by BepiPred 3.0 score"
                  >
                    BepiPred 3.0 <SortIcon field="BepiPred_3_0" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Chou_Fasman")}
                    title="Click to sort by Chou & Fasman score"
                  >
                    Chou & Fasman <SortIcon field="Chou_Fasman" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Emini")}
                    title="Click to sort by Emini score"
                  >
                    Emini <SortIcon field="Emini" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Karplus_Schulz")}
                    title="Click to sort by Karplus & Schulz score"
                  >
                    Karplus & Schulz <SortIcon field="Karplus_Schulz" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Kolaskar_Tongaonkar")}
                    title="Click to sort by Kolaskar & Tongaonkar score"
                  >
                    Kolaskar & Tongaonkar <SortIcon field="Kolaskar_Tongaonkar" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("Parker")}
                    title="Click to sort by Parker score"
                  >
                    Parker <SortIcon field="Parker" />
                  </th>
                  <th 
                    className="px-2 py-2 text-left cursor-pointer hover:bg-gray-200 transition-colors select-none"
                    onClick={() => handleSort("pmid")}
                    title="Click to sort by PMID (References)"
                  >
                    References (PMID) <SortIcon field="pmid" />
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <LoadingTable message={`Loading ${activeTab} T Cell epitopes...`} />
            ) : currentRecords.length === 0 ? (
              <tr>
                <td
                  colSpan={activeTab === "Cytotoxic" ? 10 : activeTab === "Helper" ? 10 : 12}
                  className="px-4 py-8 text-center text-gray-500 italic"
                >
                  No data available.
                </td>
              </tr>
            ) : (
              currentRecords.map((row, index) => {
                const assayCount = row.Assay ?? 0;

                return (
                  <tr key={`${row.Epitope_Name}-${index}`} className="border-b">
                    <td className="px-2 py-2">
                      <Link 
                        href={`/epitope/${encodeURIComponent(row.Epitope_Name)}?type=${activeTab}`}
                        className="text-blue-500 hover:underline hover:text-blue-600 transition"
                      >
                        {row.Epitope_Name}
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-blue-500 hover:underline">
                      {row.count_protein ? (
                        <Link href={`/protein?epitope=${encodeURIComponent(row.Epitope_Name)}`}>{row.count_protein}</Link>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="px-2 py-2 text-blue-500 hover:underline">
                      {row.count_Genome ? (
                        <Link href={`/asfv-genome?epitope=${encodeURIComponent(row.Epitope_Name)}`}>{row.count_Genome}</Link>
                      ) : (
                        0
                      )}
                    </td>
                    <td className="px-2 py-2">{row.Epitope_Source_Molecule}</td>
                    <td className="px-2 py-2">
                      {assayCount > 0 ? (
                        <Link
                          href={{
                            pathname: "assays",
                            query: { 
                              Epitope_Name: row.Epitope_Name,
                              Epitope_Source_Molecule: row.Epitope_Source_Molecule
                            },
                          }}
                          className="text-blue-500 hover:underline font-semibold"
                        >
                          {assayCount}
                        </Link>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>

                    {activeTab === "Cytotoxic" && (
                      <>
                        <td className="px-2 py-2">
                          {row.NetMHCpan_41_EL !== null && row.NetMHCpan_41_EL !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getNetMHCpanELColor(row.NetMHCpan_41_EL)}`}>
                              {row.NetMHCpan_41_EL}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {row.NetMHCpan_41_BA !== null && row.NetMHCpan_41_BA !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getNetMHCpanBAColor(row.NetMHCpan_41_BA)}`}>
                              {row.NetMHCpan_41_BA}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {row.TCRLen !== null && row.TCRLen !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getTCRLensColor(row.TCRLen)}`}>
                              {row.TCRLen.toFixed(2)}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {row.Allele ? (
                            (() => {
                              const alleles = [...new Set(row.Allele.split(/[,;]\s*/))].filter(a => a && a !== '-');
                              const isExpanded = expandedAlleles.has(index);
                              
                              if (alleles.length === 0) return "-";
                              if (alleles.length === 1) return <div className="max-w-xs">{alleles[0]}</div>;
                              
                              return (
                                <div className="max-w-xs">
                                  <div className="flex items-center gap-2">
                                    <span>{isExpanded ? alleles.join(', ') : alleles[0]}</span>
                                    <button
                                      onClick={() => toggleAlleleExpansion(index)}
                                      className="text-blue-500 hover:text-blue-700 text-xs font-semibold px-2 py-1 rounded border border-blue-300 hover:bg-blue-50 transition"
                                      title={isExpanded ? "Show less" : `Show ${alleles.length - 1} more`}
                                    >
                                      {isExpanded ? '−' : `+${alleles.length - 1}`}
                                    </button>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {row.pmid ? (
                            <div className="flex flex-wrap gap-1">
                              {row.pmid.split(', ').map((pmid: string, idx: number) => (
                                <a
                                  key={idx}
                                  href={`https://pubmed.ncbi.nlm.nih.gov/${pmid.trim()}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:underline hover:text-blue-600 transition font-medium"
                                  title={`View PMID ${pmid.trim()} on PubMed`}
                                >
                                  {pmid.trim()}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </>
                    )}
                    {activeTab === "Helper" && (
                      <>
                        <td className="px-2 py-2">
                          {row.NetMHCIIpan_4_3_EL !== null && row.NetMHCIIpan_4_3_EL !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getNetMHCIIpanELColor(row.NetMHCIIpan_4_3_EL)}`}>
                              {row.NetMHCIIpan_4_3_EL}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {row.NetMHCIIpan_4_3_BA !== null && row.NetMHCIIpan_4_3_BA !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getNetMHCIIpanBAColor(row.NetMHCIIpan_4_3_BA)}`}>
                              {row.NetMHCIIpan_4_3_BA}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>
                        <td className="px-2 py-2">{row.Affinity ?? "NA"}</td>
                        <td className="px-2 py-2">
                          {row.Allele ? (
                            (() => {
                              const alleles = [...new Set(row.Allele.split(/[,;]\s*/))].filter(a => a && a !== '-');
                              const isExpanded = expandedAlleles.has(index);
                              
                              if (alleles.length === 0) return "-";
                              if (alleles.length === 1) return <div className="max-w-xs">{alleles[0]}</div>;
                              
                              return (
                                <div className="max-w-xs">
                                  <div className="flex items-center gap-2">
                                    <span>{isExpanded ? alleles.join(', ') : alleles[0]}</span>
                                    <button
                                      onClick={() => toggleAlleleExpansion(index)}
                                      className="text-blue-500 hover:text-blue-700 text-xs font-semibold px-2 py-1 rounded border border-blue-300 hover:bg-blue-50 transition"
                                      title={isExpanded ? "Show less" : `Show ${alleles.length - 1} more`}
                                    >
                                      {isExpanded ? '−' : `+${alleles.length - 1}`}
                                    </button>
                                  </div>
                                </div>
                              );
                            })()
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {row.pmid ? (
                            <div className="flex flex-wrap gap-1">
                              {row.pmid.split(', ').map((pmid: string, idx: number) => (
                                <a
                                  key={idx}
                                  href={`https://pubmed.ncbi.nlm.nih.gov/${pmid.trim()}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:underline hover:text-blue-600 transition font-medium"
                                  title={`View PMID ${pmid.trim()} on PubMed`}
                                >
                                  {pmid.trim()}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </>
                    )}
                    {activeTab === "B" && (
                      <>
                        <td className="px-2 py-2">
                          {row.Chou_Fasman !== null && row.Chou_Fasman !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getBepiPredColor(row.Chou_Fasman)}`}>
                              {row.Chou_Fasman}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>

                        <td className="px-2 py-2">
                          {row.Emini !== null && row.Emini !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getEminiColor(row.Emini)}`}>
                              {row.Emini}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>

                        <td className="px-2 py-2">
                          {row.Karplus_Schulz !== null && row.Karplus_Schulz !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getKarplusSchulzColor(row.Karplus_Schulz)}`}>
                              {row.Karplus_Schulz}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>

                        <td className="px-2 py-2">
                          {row.Kolaskar_Tongaonkar !== null && row.Kolaskar_Tongaonkar !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getKolaskarTongaonkarColor(row.Kolaskar_Tongaonkar)}`}>
                              {row.Kolaskar_Tongaonkar}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>

                        <td className="px-2 py-2">
                          {row.Parker !== null && row.Parker !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getParkerColor(row.Parker)}`}>
                              {row.Parker}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>

                        <td className="px-2 py-2">
                          {row.BepiPred_3_0 !== null && row.BepiPred_3_0 !== undefined ? (
                            <span className={`px-2 py-1 rounded ${getBepiPredColor(row.BepiPred_3_0)}`}>
                              {row.BepiPred_3_0}
                            </span>
                          ) : (
                            "NA"
                          )}
                        </td>
                        <td className="px-2 py-2">
                          {row.pmid ? (
                            <div className="flex flex-wrap gap-1">
                              {row.pmid.split(', ').map((pmid: string, idx: number) => (
                                <a
                                  key={idx}
                                  href={`https://pubmed.ncbi.nlm.nih.gov/${pmid.trim()}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-500 hover:underline hover:text-blue-600 transition font-medium"
                                  title={`View PMID ${pmid.trim()} on PubMed`}
                                >
                                  {pmid.trim()}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                      </>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="mt-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <label htmlFor="recordsPerPage" className="text-sm">
            Records per page:
          </label>
          <select
            id="recordsPerPage"
            value={recordsPerPage}
            onChange={handleRecordsPerPageChange}
            className="border px-2 py-1 rounded"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>

        <div className="flex space-x-2">
          <button
            onClick={() => paginate(1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            First
          </button>
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-3 py-1">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Next
          </button>
          <button
            onClick={() => paginate(totalPages)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border rounded disabled:opacity-50"
          >
            Last
          </button>
        </div>
      </div>
    </div>
  );
};

export default EpitopePage;
